import { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import SearchTile from 'components/SearchKit/SearchTiles/SearchTile'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Clickable from 'components/Common/Clickable'
import Label from 'LuxKit/Label/Label'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceRowLuxPlusPriceLoadingSkeleton from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPriceLoadingSkeleton'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceLoadingSkeleton'
import { useInView } from 'react-intersection-observer'
import { trackEvent } from 'analytics/analytics'
import { promotionClickEvent, promotionViewEvent } from 'analytics/snowplow/events'
import { MALDIVES_CAMPAIGN_NAME, MALDIVES_MARKETING_BANNER_IMAGE_ID } from 'constants/charterFlight'
import useCharterFlightLabels from 'hooks/Flights/useCharterFlightLabels'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LineCalendarCheckIcon from 'LuxKit/Icons/line/LineCalendarCheckIcon'
import LineClockWithTickIcon from 'LuxKit/Icons/line/LineClockWithTickIcon'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'

const StyledClickable = styled(Clickable)`
  margin-bottom: -${rem(16)};;

  ${mediaQueryUp.tablet} {
    margin-bottom: 0;
  }
`

interface Props {
  onViewBundlesClick: () => void
  price: number
  nights: number
  isPriceLoading?: boolean
}

function MaldivesBanner({ onViewBundlesClick, price, nights, isPriceLoading = false }: Props) {
  const intl = useIntl()
  const { maldivesBundleLabel } = useCharterFlightLabels()
  const isLuxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)

  // Intersection observer to track when banner comes into view
  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.5,
  })

  // Track promotion impression when banner comes into view
  useEffect(() => {
    if (inView) {
      trackEvent(promotionViewEvent({
        promotionContext: {
          promotionName: MALDIVES_CAMPAIGN_NAME,
          promotionType: 'banner',
        },
      }))
    }
  }, [inView])

  // Track promotion click when customer clicks the banner
  const handleBannerClick = useCallback(() => {
    trackEvent(promotionClickEvent({
      promotionContext: {
        promotionName: MALDIVES_CAMPAIGN_NAME,
        promotionType: 'banner',
      },
    }))
    onViewBundlesClick()
  }, [onViewBundlesClick])

  const transitSavingsText = intl.formatMessage({
    id: '2xRdNFGLmgVqeq9rNWQt',
    defaultMessage: 'Save up to 20 hours in transit with nonstop flights from Melbourne to Malé in less than 12 hours.',
    description: 'Feature text highlighting nonstop flight time savings to Maldives on the banner',
  })

  const privateCharterText = intl.formatMessage({
    id: '0uWvmpjJ58th0kcfiFq9',
    defaultMessage: 'Luxury Escapes private charter aboard a Maldivian Airlines Airbus A330-200.',
    description: 'Feature text about the private charter service on the hotel search page',
  })

  const maximiseHolidayText = intl.formatMessage({
    id: 'cnFaVlx2lTkP1XL-Co1O',
    defaultMessage: 'Maximise your holiday time with early check-in & late checkout.',
    description: 'Feature text about early check-in and late checkout',
  })

  const freeCancellationText = intl.formatMessage({
    id: 'HGrcUkB9Yh3aHc6F7AO2',
    defaultMessage: 'Free cancellation within 7 days. Conditions apply.',
    description: 'Feature text about cancellation policy in the cancellation or refund flow',
  })

  const viewBundleOffersText = intl.formatMessage({
    id: 'fzRXJhHIxLH3AYxDm4u1',
    defaultMessage: 'View bundle offers',
    description: 'Button text to view Maldives bundle offers on the offer page',
  })

  const nightsAndFlightsFromText = intl.formatMessage({
    id: 'QW3Bl77dFXM21HCRsFBW',
    defaultMessage: '{nights} nights & flights from',
    description: 'Price caption showing number of nights and flights included',
  }, { nights })

  const bannerTitle = intl.formatMessage({
    id: 'bRLWzadFApIR2qOInkpX',
    defaultMessage: 'Maldives Hotel Bundles with Australia’s First & Only Direct Flights to Malé',
    description: 'Title for the Maldives hotel bundles banner in the flights flow',
  })
  const bannerImageTitle = intl.formatMessage({
    id: 'fJvf6zcFcEkdD5bdB15V',
    defaultMessage: 'Maldives luxury resort with overwater bungalows',
    description: 'Alternative text fallback for the Maldives hotel bundles banner image',
  })
  const staticImages = useMemo<Array<App.Image>>(() => [{
    id: MALDIVES_MARKETING_BANNER_IMAGE_ID,
    title: bannerImageTitle,
  }], [bannerImageTitle])
  const staticOffer = useMemo<App.HotelOffer>(() => ({
    id: 'maldives-banner-static',
    type: 'hotel',
    parentType: 'hotel',
    name: bannerTitle,
    slug: 'maldives-direct-banner',
    productType: 'hotel',
    location: 'Maldives',
    locations: ['Maldives'],
    saleUnit: 'package',
  } as App.HotelOffer), [bannerTitle])

  const features = (
    <Group direction="vertical" gap={4}>
      <BodyTextBlock variant="medium" startIcon={<LinePlaneDepartureIcon />}>
        {transitSavingsText}
      </BodyTextBlock>
      <BodyTextBlock variant="medium" startIcon={<LineStarIcon />}>
        {privateCharterText}
      </BodyTextBlock>
      <BodyTextBlock variant="medium" startIcon={<LineClockWithTickIcon />}>
        {maximiseHolidayText}
      </BodyTextBlock>
      <BodyTextBlock variant="medium" startIcon={<LineCalendarCheckIcon />}>
        {freeCancellationText}
      </BodyTextBlock>
    </Group>
  )

  const priceDisplay = isPriceLoading
    ? (isLuxPlusEnabled
        ? <PriceRowPriceLoadingSkeleton size="L" />
        : <PriceRowLuxPlusPriceLoadingSkeleton size="L" />)
    : (!isLuxPlusEnabled
        ? <PriceRowPrice size="L" price={price} saleUnit="person" />
        : <PriceRowLuxPlusPrice size="L" memberPrice={price} saleUnit="person" logoPlacement="trailing" />)

  const pricePoint = (
    <Group direction="vertical" gap={4}>
      <Group direction="horizontal" gap={4} verticalAlign="center">
        <LinePlaneDepartureIcon size="XS" colour="light-foreground-alt" />
        <PriceRowPriceCaption>
          {nightsAndFlightsFromText}
        </PriceRowPriceCaption>
      </Group>
      {priceDisplay}
    </Group>
  )

  return (
    <StyledClickable onClick={handleBannerClick}>
      <SearchTile
        ref={inViewRef}
        offer={staticOffer}
        images={staticImages}
        title={bannerTitle}
        features={features}
        pricePoint={pricePoint}
        action={<TextButton nonInteractable kind="primary">{viewBundleOffersText}</TextButton>}
        productLabel={<Label kind="primary" variant="maldives-charter">{maldivesBundleLabel}</Label>}
        background="highlight-primary"
      />
    </StyledClickable>
  )
}

export default MaldivesBanner
